import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Hexagon, Menu, X, Shield } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { ProfileMenu } from "@/components/profile/ProfileMenu";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();
  const { isAdmin, isInstructor } = useUserRole();
  const { t } = useTranslation();

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-card/40 backdrop-blur-2xl shadow-glass border-b border-white/10"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto px-4 lg:px-8 max-w-full">
        <div className="flex lg:grid lg:grid-cols-[auto_1fr_auto] items-center justify-between lg:gap-4 h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="Learn With Dr. Jose Silva - Home">
            <div className="bg-gradient-primary p-2 rounded-xl shadow-glass border border-white/10 group-hover:shadow-glass-hover transition-all duration-300">
              <Hexagon className="w-5 h-5 text-primary-foreground" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent" aria-hidden="true">
              AICAVITE
            </span>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center gap-6" role="menubar">
            <Link
              to="/"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.home")}
            </Link>
            <Link
              to="/courses"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.courses")}
            </Link>
            <Link
              to="/pricing"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.pricing")}
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.about")}
            </Link>
            <Link
              to="/blog"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.blog")}
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors py-2"
              role="menuitem"
            >
              {t("navigation.contact")}
            </Link>
          </div>

          {/* Auth Buttons - Fixed Right */}
          <div className="hidden lg:flex items-center justify-end gap-4 w-[280px] flex-shrink-0">
            {loading ? (
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
                <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
                <div className="w-10 h-10 rounded-full bg-muted animate-pulse" />
              </div>
            ) : user ? (
              <>
                <LanguageSwitcher />
                <NotificationBell />
                <ProfileMenu user={user} />
              </>
            ) : (
              <>
                <LanguageSwitcher />
                <Link to="/auth">
                  <Button variant="ghost" size="default">
                    {t("navigation.login")}
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="default" size="default">
                    {t("navigation.signUp")}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Auth & Menu Button */}
          <div className="flex lg:hidden items-center gap-3">
            {loading ? (
              <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
            ) : user ? (
              <>
                <LanguageSwitcher />
                <NotificationBell />
                <ProfileMenu user={user} />
              </>
            ) : (
              <LanguageSwitcher />
            )}
            <button
              className="text-foreground p-2 hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu with proper spacing */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden py-6 space-y-2 border-t border-white/10 bg-card/20 backdrop-blur-xl rounded-b-2xl animate-slide-in-from-top"
            role="menu"
          >
            <Link
              to="/"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              role="menuitem"
            >
              {t("navigation.home")}
            </Link>
            <Link
              to="/courses"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.courses")}
            </Link>
            <Link
              to="/pricing"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.pricing")}
            </Link>
            <Link
              to="/about"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.about")}
            </Link>
            {user && (
              <Link
                to="/announcements"
                className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t("navigation.announcements")}
              </Link>
            )}
            <Link
              to="/blog"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.blog")}
            </Link>
            <Link
              to="/contact"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.contact")}
            </Link>
            <Link
              to="/support"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.support")}
            </Link>
            <Link
              to="/faq"
              className="block px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-muted/30 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t("navigation.faq")}
            </Link>
            <div className="flex flex-col gap-3 pt-4 px-4 border-t border-white/10 mt-4">
              {loading ? (
                <div className="w-full h-10 bg-muted animate-pulse rounded-lg" />
              ) : user ? (
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="default" size="default" className="w-full">
                    {t("navigation.dashboard")}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" size="default" className="w-full">
                      {t("navigation.login")}
                    </Button>
                  </Link>
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="default" size="default" className="w-full">
                      {t("navigation.signUp")}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export { Navigation };
export default Navigation;
